import { useEffect } from 'react';
import {
  Button,
  Drawer,
  Group,
  MultiSelect,
  Select,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { Person } from '../../api/types';
import { useSession } from '../../app/session';
import { fromApiDate, toApiDate } from '../../lib/format';
import { applyFieldErrors, notifyError, notifySaved } from '../../lib/notify';

type FormValues = {
  first_name: string;
  middle_name: string;
  last_name: string;
  date_of_birth: Date | null;
  place_of_birth: string;
  nationality: string;
  national_id: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postal_code: string;
  country: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  staff_kinds: string[];
  pilot_status: string;
  notes: string;
};

const BLANK: FormValues = {
  first_name: '',
  middle_name: '',
  last_name: '',
  date_of_birth: null,
  place_of_birth: '',
  nationality: 'ME',
  national_id: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  postal_code: '',
  country: 'ME',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  staff_kinds: [],
  pilot_status: '',
  notes: '',
};

/**
 * Creating or editing a person.
 *
 * Choosing a pilot status on creation also creates the pilot profile, which is what makes the
 * person show up in crew pickers and currency reports.
 */
export function PersonFormDrawer({
  opened,
  onClose,
  person,
}: {
  opened: boolean;
  onClose: () => void;
  person?: Person | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enumChoices } = useSession();

  const form = useForm<FormValues>({
    initialValues: BLANK,
    validate: {
      first_name: (value) => (value.trim() ? null : t('common.requiredField')),
      last_name: (value) => (value.trim() ? null : t('common.requiredField')),
      email: (value) =>
        !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : t('auth.email'),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues(
      person
        ? {
            first_name: person.first_name,
            middle_name: person.middle_name ?? '',
            last_name: person.last_name,
            date_of_birth: fromApiDate(person.date_of_birth),
            place_of_birth: person.place_of_birth ?? '',
            nationality: person.nationality ?? '',
            national_id: person.national_id ?? '',
            email: person.email ?? '',
            phone: person.phone ?? '',
            address: person.address ?? '',
            city: person.city ?? '',
            postal_code: person.postal_code ?? '',
            country: person.country ?? '',
            emergency_contact_name: person.emergency_contact_name ?? '',
            emergency_contact_phone: person.emergency_contact_phone ?? '',
            staff_kinds: person.staff_kinds ?? [],
            pilot_status: person.pilot_profile?.status ?? '',
            notes: person.notes ?? '',
          }
        : BLANK,
    );
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, person?.id]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const body = {
        first_name: values.first_name.trim(),
        middle_name: values.middle_name.trim(),
        last_name: values.last_name.trim(),
        date_of_birth: toApiDate(values.date_of_birth),
        place_of_birth: values.place_of_birth.trim(),
        nationality: values.nationality.toUpperCase(),
        national_id: values.national_id.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        address: values.address.trim(),
        city: values.city.trim(),
        postal_code: values.postal_code.trim(),
        country: values.country.toUpperCase(),
        emergency_contact_name: values.emergency_contact_name.trim(),
        emergency_contact_phone: values.emergency_contact_phone.trim(),
        staff_kinds: values.staff_kinds,
        notes: values.notes,
      };
      if (person) {
        return unwrap(
          await api.PATCH('/api/v1/people/{id}/', {
            params: { path: { id: person.id } },
            body,
          }),
        );
      }
      return unwrap(
        await api.POST('/api/v1/people/', {
          body: { ...body, pilot_status: values.pilot_status },
        }),
      );
    },
    onSuccess: async () => {
      notifySaved();
      await queryClient.invalidateQueries({ queryKey: ['people'] });
      await queryClient.invalidateQueries({ queryKey: ['person'] });
      onClose();
    },
    onError: (error: ApiError) => {
      if (!applyFieldErrors(error, form.setErrors)) notifyError(error);
    },
  });

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={person ? person.full_name : t('staff.addPerson')}
    >
      <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
        <Stack gap="md">
          <Group grow align="flex-start">
            <TextInput
              label={t('staff.firstName')}
              required
              {...form.getInputProps('first_name')}
            />
            <TextInput label={t('staff.lastName')} required {...form.getInputProps('last_name')} />
          </Group>

          <Group grow align="flex-start">
            <DatePickerInput
              label={t('staff.dateOfBirth')}
              valueFormat="DD.MM.YYYY"
              maxDate={new Date()}
              clearable
              {...form.getInputProps('date_of_birth')}
            />
            <TextInput
              label={t('staff.nationality')}
              maxLength={2}
              w={90}
              {...form.getInputProps('nationality')}
            />
          </Group>

          <Group grow align="flex-start">
            <TextInput label={t('staff.email')} type="email" {...form.getInputProps('email')} />
            <TextInput label={t('staff.phone')} {...form.getInputProps('phone')} />
          </Group>

          <MultiSelect
            label={t('staff.title')}
            description={t('staff.staffKindsHint')}
            data={enumChoices('staff_kind').map((choice) => ({
              value: choice.value,
              label: choice.label,
            }))}
            searchable
            {...form.getInputProps('staff_kinds')}
          />

          {!person ? (
            <Select
              label={t('staff.pilotStatus')}
              data={enumChoices('pilot_status').map((choice) => ({
                value: choice.value,
                label: choice.label,
              }))}
              clearable
              {...form.getInputProps('pilot_status')}
            />
          ) : null}

          <Group grow align="flex-start">
            <TextInput label={t('staff.address')} {...form.getInputProps('address')} />
            <TextInput label={t('staff.city')} {...form.getInputProps('city')} />
          </Group>

          <Group grow align="flex-start">
            <TextInput
              label={t('staff.emergencyContact')}
              {...form.getInputProps('emergency_contact_name')}
            />
            <TextInput
              label={t('staff.phone')}
              {...form.getInputProps('emergency_contact_phone')}
            />
          </Group>

          <Textarea label={t('common.notes')} autosize minRows={2} {...form.getInputProps('notes')} />

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('action.cancel')}
            </Button>
            <Button type="submit" loading={save.isPending}>
              {t('action.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
